import React, { useState } from "react";
import {
  Card,
  Button,
  Form,
  Input,
  Select,
  Space,
  Table,
  Switch,
  Row,
  Col,
  message,
  Alert,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import { Component, PRICING_EVENTS } from "../../types/pricing";

import { trackEvent, captureError } from "../../lib/posthog";

const { Option } = Select;

interface ComponentsTabProps {
  components: Component[];
  loading: boolean;
  fetchComponents: () => Promise<void>;
}

interface ComponentFormValues {
  name: string;
  description?: string;
  type: string;
  calculation_type: string;
  is_active?: boolean;
}

const ComponentsTab: React.FC<ComponentsTabProps> = ({
  components,
  loading,
  fetchComponents,
}) => {
  const [componentForm] = Form.useForm<ComponentFormValues>();
  const [editingComponent, setEditingComponent] = useState<Component | null>(
    null
  );

  const handleComponentSubmit = async (values: ComponentFormValues) => {
    try {
      // Map form values to database structure
      const componentData = {
        name: values.name,
        description: values.description,
        component_type: values.type,
        calculation_type: values.calculation_type || "fixed", // default to fixed
      };

      if (editingComponent) {
        // Update existing component
        const { error } = await supabase
          .from("pricing_components")
          .update(componentData)
          .eq("id", editingComponent.id);

        if (error) throw error;
        message.success("Component updated successfully");

        // Track component update
        trackEvent(PRICING_EVENTS.UPDATE_COMPONENT, {
          component_id: editingComponent.id,
          component_name: values.name,
          component_type: values.type,
        });
      } else {
        // Insert new component
        const { data, error } = await supabase
          .from("pricing_components")
          .insert(componentData)
          .select();

        if (error) throw error;
        message.success("Component added successfully");

        // Track component creation
        trackEvent(PRICING_EVENTS.ADD_COMPONENT, {
          component_id: data?.[0]?.id,
          component_name: values.name,
          component_type: values.type,
        });
      }

      // Refresh the components list
      fetchComponents();
      setEditingComponent(null);
      componentForm.resetFields();
    } catch (error: unknown) {
      console.error("Error saving component:", error);
      message.error("Failed to save component");
      captureError(error, {
        source: "ComponentsTab",
        action: editingComponent ? "updateComponent" : "createComponent",
        componentData: values,
      });
    }
  };

  const handleEditComponent = (record: Component) => {
    console.log("[DEBUG] Editing component:", record);
    setEditingComponent(record);
    componentForm.setFieldsValue({
      name: record.name,
      description: record.description,
      type: record.component_type || "",
      calculation_type: record.calculation_type || "",
    });
  };

  const handleDeleteComponent = async (id: string) => {
    try {
      const { error } = await supabase
        .from("pricing_components")
        .delete()
        .eq("id", id);

      if (error) throw error;

      message.success("Component deleted successfully");

      // Track component deletion
      trackEvent(PRICING_EVENTS.DELETE_COMPONENT, {
        component_id: id,
      });

      fetchComponents();
    } catch (error: unknown) {
      console.error("Error deleting component:", error);
      message.error("Failed to delete component");
      captureError(error, {
        source: "ComponentsTab",
        action: "deleteComponent",
        componentId: id,
      });
    }
  };

  const componentColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Calculation",
      dataIndex: "calculation_type",
      key: "calculation_type",
    },

    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (active: boolean) => <span>{active ? "Yes" : "No"}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: Component) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEditComponent(record)}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteComponent(record.id.toString())}
          />
        </Space>
      ),
    },
  ];

  return (
    <Row gutter={24}>
      <Col span={10}>
        <Card
          title={editingComponent ? "Edit Component" : "Add New Component"}
          extra={
            <Alert
              type="info"
              showIcon
              message="True Inheritance System"
              description="All components are automatically inherited by child nodes in the pricing tree. No behavior settings needed!"
              style={{ marginBottom: 0, fontSize: "12px" }}
              banner
            />
          }
        >
          <Form
            form={componentForm}
            layout="vertical"
            onFinish={handleComponentSubmit}
          >
            <Form.Item
              name="name"
              label="Component Name"
              rules={[{ required: true, message: "Please enter a name" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item
              name="type"
              label="Component Type"
              rules={[{ required: true, message: "Please select a type" }]}
            >
              <Select>
                <Option value="base">Base</Option>
                <Option value="fee">Fee</Option>
                <Option value="surcharge">Surcharge</Option>
                <Option value="service">Service</Option>
                <Option value="insurance">Insurance</Option>
                <Option value="discount">Discount</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="calculation_type"
              label="Calculation Type"
              rules={[
                {
                  required: true,
                  message: "Please select a calculation type",
                },
              ]}
            >
              <Select>
                <Option value="fixed">Fixed Amount</Option>
                <Option value="percentage">Percentage</Option>
                <Option value="per_cbm">Per CBM</Option>
                <Option value="per_weight">Per KG</Option>
                <Option value="per_product">Per Product</Option>
                <Option value="per_unique_product">
                  Per Unique Product (HS Code)
                </Option>
                <Option value="per_carton">Per Carton</Option>
                <Option value="per_pallet">Per Pallet</Option>
                <Option value="per_shipment">Per Shipment</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="is_active"
              label="Active"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingComponent ? "Update" : "Add"} Component
                </Button>
                {editingComponent && (
                  <Button
                    onClick={() => {
                      setEditingComponent(null);
                      componentForm.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>

      <Col span={14}>
        <Card title="Pricing Components">
          <Table
            columns={componentColumns}
            dataSource={components}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ y: 400 }}
            loading={loading}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default ComponentsTab;
